import { type SelectOption } from '@ibe/ui';

/**
 * The dialling-code picker, built from the countries the server already sent.
 *
 * Derived rather than declared: the prefixes live on `countries.phone_prefix`,
 * and a list compiled into this app would be a second copy that drifts the
 * first time a territory changes hands. Both forms that collect a dialling
 * code call this with their own module's country list.
 *
 * Shared by the tenant and property forms because they ask the same question
 * of the same data, and two copies of this grouping would answer it two ways.
 */

/** The shape both modules' country options happen to have. */
export interface DialCodeCountry {
  readonly name: string;
  readonly phonePrefix: string | null;
}

/**
 * Countries that share a prefix become one option, because the value stored is
 * the prefix and two rows carrying `+1` cannot be told apart once chosen.
 *
 * Every name stays in the label rather than being summarised away — the picker
 * filters on the label, so `+1876 Jamaica` has to say Jamaica to be findable,
 * and `+7` has to say both Russia and Kazakhstan. The label is truncated on
 * screen; it is never truncated for the search.
 */
export function dialCodeOptions(
  countries: readonly DialCodeCountry[],
  /**
   * What the form currently holds.
   *
   * A tenant saved before this was a picker may carry a prefix no country
   * claims, or one typed without its `+`. Passing it keeps that value in the
   * list, so opening the form shows what is stored rather than an empty
   * control that looks like nothing was ever set.
   */
  current?: string,
): readonly SelectOption[] {
  const names = new Map<string, string[]>();

  for (const country of countries) {
    if (country.phonePrefix === null || country.phonePrefix === '') continue;
    const group = names.get(country.phonePrefix);
    if (group === undefined) {
      names.set(country.phonePrefix, [country.name]);
    } else {
      group.push(country.name);
    }
  }

  const options = [...names.entries()]
    .map(([prefix, group]) => {
      const sorted = [...group].sort((a, b) => a.localeCompare(b));
      return { value: prefix, label: `${prefix} ${sorted.join(', ')}`, first: sorted[0] ?? '' };
    })
    // Ordered by country, not by the arithmetic of the prefix: someone
    // scrolling for Australia looks under A, never under 61.
    .sort((a, b) => a.first.localeCompare(b.first))
    .map(({ value, label }) => ({ value, label }));

  if (current !== undefined && current !== '' && !names.has(current)) {
    return [{ value: current, label: current }, ...options];
  }

  return options;
}
